import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { applyPageMeta, SITE_URL } from '../components/SEOMetaTags';

/**
 * Pagină hub: lista tuturor orașelor cu pagină de extras carte funciară
 * URL: /extras-carte-funciara/
 * Rol SEO: link-uri interne crawlabile spre TOATE paginile de oraș (descoperire + indexare)
 */

const PAGE_META = {
  title: 'Extras Carte Funciară în Orașul Tău – Listă Orașe',
  description: 'Alege orașul și comandă extras de carte funciară online: document oficial pe email în 5 minute, pentru orice imobil din România.',
  canonical: `${SITE_URL}/extras-carte-funciara/`
};

function LocationsHub() {
  const [cities, setCities] = useState([]);

  // Meta setate sincron (ajung în HTML-ul pre-randat)
  useEffect(() => {
    applyPageMeta(PAGE_META);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch('/seo-data/locations-data.json');
        const data = await res.json();
        const seen = new Set();
        const unique = (data.cities || []).filter(c => {
          if (!c.slug || seen.has(c.slug)) return false;
          seen.add(c.slug);
          return true;
        });
        setCities(unique);
      } catch (e) {
        console.error('Eroare încărcare orașe:', e);
      }
    };
    load();
  }, []);

  // Grupare pe județe (ordine alfabetică română)
  const byCounty = cities.reduce((acc, c) => {
    const county = c.county || 'Altele';
    (acc[county] = acc[county] || []).push(c);
    return acc;
  }, {});
  const counties = Object.keys(byCounty).sort((a, b) => a.localeCompare(b, 'ro'));
  const topCities = cities.slice(0, 12); // primele din listă = cele mai mari orașe

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-cyan-50">
      <Header />

      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-8">
          <nav className="text-sm mb-4">
            <ol className="flex items-center space-x-2 text-gray-600">
              <li>
                <Link to="/" className="hover:text-emerald-600 transition">Acasă</Link>
              </li>
              <li>/</li>
              <li className="text-gray-900 font-medium">Orașe</li>
            </ol>
          </nav>
          <div className="flex items-center space-x-3 mb-4">
            <MapPin className="h-8 w-8 text-emerald-600" />
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Extras Carte Funciară în Orașul Tău
            </h1>
          </div>
          <p className="text-lg text-gray-600 max-w-3xl">
            Comanzi extrasul de carte funciară online, pentru orice imobil din România: completezi
            numărul cadastral, plătești cu cardul și primești documentul oficial pe email în aproximativ
            5 minute. Alege orașul pentru detalii locale.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 space-y-10">
        {/* Orașe mari */}
        {topCities.length > 0 && (
          <section className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Cele mai căutate orașe</h2>
            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {topCities.map(c => (
                <li key={c.slug}>
                  <Link
                    to={`/extras-carte-funciara/${c.slug}/`}
                    className="flex items-center gap-2 px-4 py-3 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 font-medium hover:bg-emerald-100 transition"
                  >
                    <MapPin className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                    Extras CF {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Toate orașele, pe județe */}
        <section className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Toate orașele, pe județe</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {counties.map(county => (
              <div key={county}>
                <h3 className="font-semibold text-gray-900 mb-2">{county}</h3>
                <ul className="space-y-1">
                  {byCounty[county].map(c => (
                    <li key={c.slug}>
                      <Link
                        to={`/extras-carte-funciara/${c.slug}/`}
                        className="text-emerald-700 hover:text-emerald-900 hover:underline"
                      >
                        Extras carte funciară {c.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-emerald-600 rounded-2xl shadow-lg p-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-3">Orașul tău nu e în listă?</h2>
          <p className="mb-6 text-emerald-50">
            Nicio problemă: poți comanda extrasul pentru orice imobil din România, indiferent de localitate.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-emerald-700 font-semibold rounded-lg hover:bg-emerald-50 transition"
          >
            <CheckCircle className="h-5 w-5" aria-hidden="true" />
            Comandă extrasul acum
          </Link>
        </section>
      </div>

      <Footer />
    </div>
  );
}

export default LocationsHub;
